/**
 * Authentication Service
 * Handles Supabase Authentication operations (Google OAuth).
 */

import { supabase } from '../../utils/supabase_config';
import { UserService } from '../user/userService';

export class AuthService {
  /**
   * Sign in with Google via Supabase OAuth (redirect flow).
   * The user will be redirected to Google and then back to the app.
   */
  static async signInWithGoogle() {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw new Error(this.getAuthErrorMessage(error));
    }
  }

  /**
   * Sign out current user.
   */
  static async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      console.log('User signed out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
      throw new Error('Failed to sign out. Please try again.');
    }
  }

  /**
   * Get the current session's user synchronously from cached session.
   * Returns null if no active session.
   */
  static getCurrentUser() {
    // supabase.auth.getUser() is async; for sync access use the cached session
    return supabase.auth.getSession().then(({ data: { session } }) => session?.user ?? null);
  }

  /**
   * Get current user's email from the active session.
   * @returns {Promise<string|null>}
   */
  static async getCurrentUserEmail() {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.user?.email ?? null;
  }

  /**
   * Get current user's ID from the active session.
   * @returns {Promise<string|null>}
   */
  static async getCurrentUserUid() {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.user?.id ?? null;
  }

  /**
   * Check if current user exists in the app's users table.
   * @returns {Promise<boolean>}
   */
  static async currentUserExistsInDatabase() {
    const email = await this.getCurrentUserEmail();
    if (!email) return false;
    try {
      const dbUser = await UserService.getUserByEmail(email);
      return !!dbUser;
    } catch (error) {
      console.error('Error checking user in database:', error);
      return false;
    }
  }

  /**
   * Get current user's record from the app's users table.
   * @returns {Promise<Object|null>}
   */
  static async getCurrentUserRecord() {
    const email = await this.getCurrentUserEmail();
    if (!email) return null;
    try {
      return await UserService.getUserByEmail(email);
    } catch (error) {
      console.error('Error getting user record:', error);
      return null;
    }
  }

  /**
   * Subscribe to auth state changes.
   * @param {Function} callback - Receives (event, session)
   * @returns {Object} Subscription with .unsubscribe() method
   */
  static onAuthStateChanged(callback) {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(callback);
    return subscription;
  }

  /**
   * Convert Supabase auth errors to user-friendly messages.
   */
  static getAuthErrorMessage(error) {
    const msg = error?.message?.toLowerCase() ?? '';
    if (msg.includes('popup')) return 'Sign-in was cancelled. Please try again.';
    if (msg.includes('network')) return 'Network error. Please check your connection and try again.';
    if (msg.includes('too many')) return 'Too many failed attempts. Please try again later.';
    return 'Authentication failed. Please try again.';
  }
}
